import React, { FC, memo, useState } from 'react';
import { ReactComponent as EmailIcon } from 'applicaiton/assets/email.svg';
import { ReactComponent as SearchIcon } from 'applicaiton/assets/search.svg';
import { ReactComponent as CloseIcon } from 'applicaiton/assets/close.svg';
import { ReactComponent as EyeOpenIcon } from 'applicaiton/assets/eye-open.svg';
import { ReactComponent as EyeClosedIcon } from 'applicaiton/assets/eye-closed.svg';
import { ReactComponent as AttentionIcon } from 'applicaiton/assets/attention.svg';
import { ReactComponent as InfoIcon } from 'applicaiton/assets/info.svg';
import { ReactComponent as CheckCircleIcon } from 'applicaiton/assets/check-circle.svg';
import { InputProps } from './models';
import { InputWrapper, StyledInput, LabelWrapper } from './styled';

/**
 * A reusable input component that supports various types of inputs.
 * This component can be used for text, email, search, and password fields,
 * and it also includes support for error messages and hints.
 * @param id - A unique identifier for the input field.
 * @param placeholder - The text to display as a placeholder inside the input field.
 * @param label - The label text for the input field.
 * @param disabled - Whether the input field is disabled or not.
 * @param errorMessage - An error message to display if there is an error with the input.
 * @param hint - A hint to display below the input field.
 * @param type - The type of input (e.g. text, email, password).
 * @param register - A function that registers the input field with a form library.
 * @param isFocused - Whether the input field should be automatically focused when rendered.
 * @param onChange - A function to call when the value of the input changes.
 * @param onClear - A function to call when the search icon is clicked and the input is cleared.
 * @param value - The current value of the input.
 * @param autoComplete - Whether autocomplete should be enabled for the input field.
 * @param showRequiredIcon - Whether a required icon should be displayed next to the label.
 * @param min - The minimum value allowed for numeric inputs.
 * @param step - The increment/decrement value for numeric inputs.
 * @param pattern - A regular expression that the input value must match.
 */
const Input: FC<InputProps> = ({
  id,
  placeholder,
  label,
  disabled,
  errorMessage,
  hint,
  type,
  register,
  isFocused,
  onChange,
  onClear,
  value,
  autoComplete,
  showRequiredIcon,
  min,
  step,
  pattern,
  successStatus,
  errorStatus,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <StyledInput
      isError={!!errorMessage}
      disabled={disabled}
      type={type}
      successStatus={successStatus}
    >
      {label && (
        <LabelWrapper>
          <label htmlFor={id}>{label}</label>
          {showRequiredIcon && <InfoIcon />}
        </LabelWrapper>
      )}
      <InputWrapper
        disabled={disabled}
        type={type}
        successStatus={successStatus}
      >
        {type === 'email' && <EmailIcon />}
        {type === 'search' && <SearchIcon />}
        {successStatus && <CheckCircleIcon />}
        {register ? (
          <input
            id={id}
            placeholder={placeholder}
            disabled={disabled}
            type={showPassword ? 'text' : type}
            {...register(id)}
            autoFocus={isFocused}
            autoComplete={autoComplete}
            min={min}
            step={step}
            pattern={pattern}
            onWheel={(e) => e.currentTarget.blur()}
          />
        ) : (
          <input
            id={id}
            placeholder={placeholder}
            disabled={disabled}
            type={showPassword ? 'text' : type}
            value={value || ''}
            onChange={onChange}
            autoFocus={isFocused}
            autoComplete={autoComplete}
            min={min}
            step={step}
            pattern={pattern}
            onWheel={(e) => e.currentTarget.blur()}
          />
        )}
        {((type === 'email' && errorMessage) || errorStatus) && (
          <span>
            <AttentionIcon />
          </span>
        )}
        {type === 'search' && value && (
          <CloseIcon onClick={() => onClear && onClear()} />
        )}
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? (
              <EyeClosedIcon width={18} height={18} />
            ) : (
              <EyeOpenIcon width={18} height={18} />
            )}
          </button>
        )}
      </InputWrapper>
      {hint ||
        (errorMessage && <span>{errorMessage ? errorMessage : hint}</span>)}
    </StyledInput>
  );
};

export default memo(Input);
